import { ChevronLeft, Menu as MenuIcon } from '@mui/icons-material';
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from '@mui/material';
import { darken, Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { FC, Fragment, useContext, useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { UserContext } from '../../context';
import {
  getEnvironment,
  getRelativeLink,
  hasNavAccess,
  hasNavActiveFlags,
  isVendorPartner,
  isExternalUser,
  isAllowedToSeeHomeownerPortal,
  isLenderOrLenderProcessor,
  filterNavItems,
  filterNavByAllowedRoutes
} from '../../helpers';
import { INavLink, UserRoles } from '../../models';
import { getAnyVendorConfigured } from '../../fetch';
import { useFlags } from '../../hooks';
import { Link } from '../link';
import { ExternalLink } from '../link/ExternalLink';
import { Logo, FullLogoWidth, FullLogoHeight } from '../skeleton';
import { CompactLogo, CompactLogoWidth, CompactLogoHeight } from '../skeleton/CompactLogo';
import { NestedItem } from '../skeleton/NestedItem';
import { useAllowedNavRoutes } from '../../hooks/useAllowedNavRoutes';

export interface ISideNavProps {
  open: boolean;
  navItems: INavLink[];
  isMobile: boolean;
  setShowNav: (b: boolean) => void;
  width: number;
  headerHeight: number;
  warningHeight: number;
  showSideNav: boolean;
  disableDesktopCollapse?: boolean;
}

export const SideNav: FC<ISideNavProps> = ({
  headerHeight,
  open,
  navItems,
  isMobile,
  setShowNav,
  showSideNav,
  width,
  warningHeight,
  disableDesktopCollapse = false
}) => {
  const userContext = useContext(UserContext);
  const { user, isLoading, canAccessFileManagement, canAccessManageDocumentGroups } =
    useContext(UserContext);
  const { pathname } = useLocation();
  const [isAnyVendorConfigured, setAnyVendorConfigured] = useState<boolean>(false);
  const classes = useStyles({
    width,
    open,
    showSideNav,
    FullLogoWidth,
    FullLogoHeight,
    CompactLogoWidth,
    CompactLogoHeight
  });
  const featureFlags = useFlags();
  const { availableLinks } = useAllowedNavRoutes(featureFlags, user);

  const filteredLinks = useMemo(() => {
    let links = navItems;
    if (featureFlags?.authorizeUserNavigationAccess && availableLinks?.size > 0) {
      links = filterNavByAllowedRoutes(links, new Set(availableLinks));
    } else {
      links = links.filter(link => hasNavAccess(link?.roles, userContext));
    }

    // Additional filtering
    if (featureFlags?.unifiedUserManagement) {
      links = filterNavItems(links, [
        'project-managers',
        'supervisors',
        'warranty-managers',
        'foremen'
      ]);
    }
    if (featureFlags?.homeownerPortalV2) {
      links = filterNavItems(links, ['homeowner-portals']);
    }
    // Special filtering for Financial Tools, must have BLFinancialTools role to see it
    links = links.map(link => {
      if (link.name === 'Admin' && link.links) {
        const filteredAdminLinks = link.links.filter(adminLink => {
          if (adminLink.name === 'Financial Tools') {
            return userContext?.user?.userRoles?.includes('BL_Financial_Tools') ?? false;
          }
          return true;
        });
        return { ...link, links: filteredAdminLinks };
      }
      return link;
    });
    // isMember must be true to see reporting links
    if (!user?.isMember) {
      links = filterNavItems(links, ['reporting']);
    }
    // One-off situation where user must have BLAdmin and BLClosing to see the file management link
    if (!canAccessFileManagement) {
      links = filterNavItems(links, ['file-management']);
    }
    // One-off situation where user must have BLAdmin and BLClosing to see the manage document groups link
    if (!canAccessManageDocumentGroups) {
      links = filterNavItems(links, ['manage-document-groups']);
    }
    if (isExternalUser(user)) {
      links = links.filter(link => !link?.isInternalOnly);
    }
    if (isLenderOrLenderProcessor(user)) {
      //remove the reporting link for lenders & lender processors
      links = links.filter(link => link.name !== 'Reporting');
    }
    // Remove the Homeowner Portals link if the user is not a BLClient or is an external user and not a BL_Agent
    if (!isAllowedToSeeHomeownerPortal(user)) {
      links = links.filter(link => link?.id !== 'homeowner-portals');
    }
    return links;
  }, [
    navItems,
    availableLinks,
    featureFlags,
    user,
    userContext,
    canAccessFileManagement,
    canAccessManageDocumentGroups
  ]);

  const [navOpen, setOpenNav] = useState<Record<string, boolean>>({
    Admin: pathname.includes('admin/') ? true : false,
    Production: pathname.includes('production/') ? true : false,
    Vendors: pathname.includes('vendors/') ? true : false,
    Documentation: pathname.includes('documentation/') ? true : false,
    Development: pathname.includes('development/') ? true : false,
    Possessions: pathname.includes('possessions/') ? true : false,
    Reporting: pathname.includes('reporting/') ? true : false,
    Sales: pathname.includes('sales/') ? true : false,
    // Style Guide
    Components: pathname.includes('components/') ? true : false,
    Buttons: pathname.includes('/buttons') ? true : false,
    Grids: pathname.includes('/grids') ? true : false
  });

  const fetchAnyVendorConfigured = async () => {
    try {
      if (user?.userRoles?.includes(UserRoles.VendorUserManagement)) {
        const res = await getAnyVendorConfigured();
        setAnyVendorConfigured(res);
      }
    } catch (error) {}
  };

  // if the logged in user has only link nav item, then open it up by default in the side nav
  useEffect(() => {
    fetchAnyVendorConfigured();
    if (filteredLinks.length === 1) {
      setOpenNav({
        [filteredLinks?.[0]?.name]: true
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const getHomeLink = () => {
    if (user && !isLoading) {
      if (isVendorPartner(user) && getEnvironment() === 'localhost') {
        return '/vendors';
      }
      return `/`;
    }
    return `/`;
  };
  const renderNavLink = (link: INavLink) => {
    const ToolTipListItem = () => (
      <Tooltip title={!open ? link.name : ''} placement="right-end">
        <ListItem>
          {link.icon && (
            <ListItemIcon
              className={clsx(
                classes.listItemIcon,
                pathname.includes(link.url) && classes.activeIcon
              )}
              classes={{ root: classes.listItemIconRoot }}
            >
              {link.icon}
            </ListItemIcon>
          )}
          <ListItemText
            style={{ letterSpacing: 2 }}
            primary={open ? link.name : ''}
            className={classes.drawerMobileListItem}
          />
        </ListItem>
      </Tooltip>
    );

    const hasLegacyLink = link.legacyUrl && !link.url ? true : false;
    const hasRelativeLink = link.url ? true : false;
    const hasLegacyLinkWithFFOff =
      link.featureFlag && link.legacyUrl && !featureFlags[link.featureFlag] ? true : false;
    const hasChildLinks = link?.links?.length > 0 ? true : false;

    /* we have child links */
    if (hasChildLinks) {
      return (
        <NestedItem
          showText={open}
          navOpen={navOpen}
          setOpenNav={setOpenNav}
          link={{
            ...link,
            // pass the correct url to the parent link in this context when it has child links
            url: hasLegacyLinkWithFFOff || hasLegacyLink ? link.legacyUrl : link.url
          }}
          isNavOpen={open}
          isAnyVendorConfigured={isAnyVendorConfigured}
        />
      );
    }
    // has a legacy URL in the top level parent link
    if (hasLegacyLink || hasLegacyLinkWithFFOff) {
      return (
        <ExternalLink to={link.legacyUrl} target="_blank">
          <ToolTipListItem />
        </ExternalLink>
      );
    }

    /* standard top level relative links */
    if (hasRelativeLink) {
      return (
        <Link
          to={getRelativeLink(link.url)}
          className={clsx(classes.drawerLink, pathname === link.url && classes.drawerLinkActive)}
        >
          <ToolTipListItem />
        </Link>
      );
    }

    /* top level nav doesn't have a link */
    return <Typography>{link.name}</Typography>;
  };

  return (
    <Drawer
      anchor="left"
      variant="permanent"
      className="print--none"
      sx={{
        width: isMobile ? 0 : width,
        display: showSideNav ? 'block' : 'none',
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: width,
          boxSizing: 'border-box',
          overflow: 'hidden'
        }
      }}
      open={(isMobile && open) || !isMobile}
    >
      <Box
        display="flex"
        justifyContent={'space-around'}
        alignItems="center"
        sx={{
          height: headerHeight,
          minHeight: headerHeight,
          marginTop: (isMobile || !showSideNav) && warningHeight ? `${warningHeight}px` : 0
        }}
      >
        {/* LOGO */}
        <Link to={getHomeLink()} className={classes.logoLink}>
          {open ? <Logo className={classes.logo} /> : <CompactLogo className={classes.logo} />}
        </Link>

        {/* MENU BUTTON */}
        {!isMobile && !disableDesktopCollapse && (
          <IconButton
            onClick={() => setShowNav(!open)}
            color="inherit"
            className={classes.menuButton}
          >
            {open ? (
              <ChevronLeft className={classes.iconBlack} />
            ) : (
              <MenuIcon className={classes.iconBlack} />
            )}
          </IconButton>
        )}
      </Box>
      <Divider />
      {/* NAV CONTAINER */}

      <Box className={classes.navContainer}>
        <List classes={classes.listRoot}>
          {filteredLinks.map((link, index) => {
            if (!!hasNavActiveFlags(link, featureFlags)) {
              return (
                <Fragment key={`${index}`}>
                  {renderNavLink(link)}
                  <Divider />
                </Fragment>
              );
            }
            return null;
          })}
        </List>
      </Box>
    </Drawer>
  );
};

const useStyles = makeStyles<
  Theme,
  {
    width: number;
    open: boolean;
    showSideNav: boolean;
    FullLogoWidth: number;
    FullLogoHeight: number;
    CompactLogoWidth: number;
    CompactLogoHeight: number;
  }
>(theme => ({
  navContainer: {
    overflowY: 'auto',
    overflowX: 'hidden',
    width: ({ width }) => width,
    transition: theme?.transitions?.create('width', {
      easing: theme?.transitions?.easing.sharp,
      duration: theme?.transitions?.duration.leavingScreen
    })
  },
  showLabel: {
    opacity: 1,
    transition: theme.transitions?.create('opacity', {
      easing: theme?.transitions?.easing.sharp,
      duration: theme?.transitions?.duration.leavingScreen
    })
  },
  hideLabel: {
    opacity: 0
  },
  drawerLinkActive: {
    color: darken(theme?.palette?.primary.main, 0.3),
    fontWeight: 'bold',
    '&& span': {
      fontWeight: 'bold'
    }
  },
  navIcon: {
    '& svg': {
      fill: 'inherit'
    }
  },
  activeIcon: {
    color: theme?.palette?.primary.main
  },
  listItemIconRoot: {
    minWidth: '1px',
    marginRight: theme?.spacing(1)
  },
  logoLink: {
    display: 'flex',
    alignItems: 'center'
  },
  logo: {
    width: ({ open, showSideNav, FullLogoWidth, CompactLogoWidth }) =>
      open || !showSideNav ? FullLogoWidth : CompactLogoWidth,
    height: ({ open, showSideNav, FullLogoHeight, CompactLogoHeight }) =>
      open || !showSideNav ? FullLogoHeight : CompactLogoHeight,
    paddingLeft: ({ open, showSideNav }) => (open || !showSideNav ? 0 : 3)
  }
}));
